import {
    CircularProgress,
    Divider,
    Grid,
    Typography
} from '@mui/material';
import { ActionButton } from '@tassat/button';
import { Cancel as CancelIcon, Search as SearchIcon } from '@tassat/icon';
import React from 'react';

import { MAX_SEARCH_RESULTS } from 'Lib/constant';
import AdminStore from 'Reducers/store/adminStore';
import ClientUserAccountList from 'SharedComponents/users/ClientUserAccountList';

import {
    CLIENT_USER_SEARCH_INITIAL_STATE,
    ClientUserSearchAction,
    ClientUserSearchReducer,
    ClientUserSearchState
} from './ClientUserSearchReducer';

import type { ClientUserSearchProps } from '.';

export default function ClientUserSearch(props: ClientUserSearchProps) {
    const { classes } = props;

    const searchWorker: Worker = new Worker(new URL('././ClientUserSearchWorker.worker.ts', import.meta.url), { type: 'module' });
    const [ clientSearchValue, setClientSearchValue ] = React.useState<string>('');
    const [ clientSearchState, dispatchClientSearchAction ] =
    React.useReducer<(state: ClientUserSearchState, action: ClientUserSearchAction) =>
    ClientUserSearchState>(ClientUserSearchReducer, CLIENT_USER_SEARCH_INITIAL_STATE);
    const inputRef = React.useRef(null);

    React.useEffect(() => {
        // Terminate worker only on component unmount
        return () => {
            if (searchWorker) {
                searchWorker.terminate();
            }
        };
    }, []);

    React.useEffect(() => {
        searchWorker.onmessage = (event: MessageEvent) => {
            if (event?.data?.type === 'ClientUserSearchResults') {
                dispatchClientSearchAction({
                    type: 'RESULTS_RECEIVED',
                    payload: event.data.results
                });
            }
        };
    }, [searchWorker]);

    React.useEffect(() => {
        if (clientSearchState.isOpen) {
            inputRef.current.focus();
        }

        if (clientSearchState.isOpen) {
            window.addEventListener('keydown', closeSearch);

            return () => window.removeEventListener('keydown', closeSearch);
        }
    }, [clientSearchState.isOpen]);

    const searchForClients = async () => {
        dispatchClientSearchAction({ type: 'START_SEARCH' });
        const state = AdminStore.getState();
        searchWorker.postMessage({
            type: 'ClientUserSearchQuery',
            storeState: state,
            searchValue: clientSearchValue
        });
    };

    const closeSearch = (e) => {
        if (e.key === 'Escape') {
            dispatchClientSearchAction({ type: 'CLEAR_SEARCH' });
            setClientSearchValue('');
        }
    };

    const renderSearchResults = () => {
        if (clientSearchState.isLoading) {
            return <CircularProgress className={classes.searchMessage} />;
        }

        if (clientSearchState.resultTotal === 0) {
            return <div className={classes.searchMessage}>No Client Users found</div>;
        }

        if (clientSearchState.resultTotal > 0) {
            return (
                <>
                    {clientSearchState.isTrimmed &&
                        (
                        <div className={`${classes.searchMessage} ${classes.trimMessage}`}>
                            <Typography
                                className={classes.messageText}
                                variant='body2'
                            >
                                Search has returned {clientSearchState.resultTotal} users,
                                only showing the top {MAX_SEARCH_RESULTS} results.
                                Please refine search term for more accurate results.
                            </Typography>
                            <Divider variant='fullWidth' />
                        </div>
                    )}
                    <div className={`${classes.clientList} ${clientSearchState.isTrimmed ? classes.listMessagePadding : ''}`}>
                        {clientSearchState.searchResults.map((mappedUser, idx) => (
                            <div key={mappedUser.user.UserId}>
                                <ClientUserAccountList mappedUser={mappedUser} />
                                {idx !== clientSearchState.searchResults.length - 1 && <Divider variant='fullWidth' />}
                            </div>
                        ))}
                    </div>
                </>
            );
        }

        return null;
    };

    return (
        <Grid
            className={classes.root}
            container
            direction='row'
            spacing={0}
            wrap='nowrap'
        >
            {!clientSearchState.isOpen ?
                <Grid
                    alignItems='center'
                    className={classes.clientSearchButton}
                    container
                    direction='row'
                    id='clientUserSearch_ClientLookupButton'
                    onClick={() => dispatchClientSearchAction({ type: 'OPEN_SEARCH' })}
                    spacing={0}
                    wrap='nowrap'
                >
                    <SearchIcon
                        className={classes.searchIcon}
                        iconColor='#4A4C50'
                    />
                    <Typography
                        className={classes.searchButtonLabel}
                        variant='body1'
                    >Client Look-up
                    </Typography>
                </Grid>
                : (
                    <Grid
                        alignItems='center'
                        className={classes.searchBarContainer}
                        container
                        direction='row'
                        spacing={0}
                        wrap='nowrap'
                    >
                        <input
                            className={classes.searchBarInput}
                            id='clientUserSearch_EmailInput'
                            maxLength={64}
                            onChange={(e) => setClientSearchValue(e.target.value.trim())}
                            placeholder='Client Email'
                            ref={inputRef}
                            type='text'
                        />
                        <ActionButton
                            handleClick={searchForClients}
                            Icon={SearchIcon}
                            iconColor='#4A4C50'
                            id='clientUserSearch_SearchButton'
                            text='Apply Search'
                            type='icon'
                        />
                        <ActionButton
                            className={classes.searchBarClose}
                            handleClick={() => {
                                dispatchClientSearchAction({ type: 'CLEAR_SEARCH' });
                                setClientSearchValue('');
                            }}
                            Icon={CancelIcon}
                            iconColor='#4A4C50'
                            id='clientUserSearch_CloseButton'
                            text='Close'
                            type='icon'
                        />
                        {(clientSearchState.isComplete || clientSearchState.isLoading) &&
                            (
                            <Grid
                                className={classes.foundUsersContainer}
                                container
                                direction='column'
                                spacing={0}
                                wrap='nowrap'
                            >
                                {renderSearchResults()}
                            </Grid>
                        )}
                    </Grid>
                )}
        </Grid>
    );
}